import React, { useCallback } from 'react';
import SimilarListings, { SimilarListingsProps } from './components/similar-listings/similar-listings';
import { useInView } from 'react-intersection-observer';
import { listingPageModalIds } from 'constants/test-constants';
import { getSimilarListings } from 'data/listing';
import styles from './style.module.scss';
import { SimilarListing, StandardStatus } from '@zoocasa/go-search';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { useQuery } from '@tanstack/react-query';
import { SimilarListingViewModel } from './similar-listing-view-model';
import { useUserContext } from 'contexts/user';

import type Listing from 'data/listing';
import type { ComparableListing } from './components/similar-listings';
import { ComplianceData, SimilarListingsComponentUsage } from './components/similar-listings/similar-listings.type';
import { getComplianceRequirementFromCookies, shouldScrambleForProvider } from 'utils/compliance';

//#region Types
type SimilarListingData = Pick<Listing, 'id'> & ComparableListing;

type SimilarListingsSectionsProps = {
  listing: SimilarListingData;
  /** The size of the similar listings to be displayed in each section. */
  size?: number;
  isOffMarket?: boolean;
  hideSimilarListings?: boolean;
  hideComparableListings?: boolean;
  showCarousel?: boolean;
  /** Optional callback function to handle clicks on listing cards. */
  onListingCardClick: SimilarListingsProps['onListingCardClick'];
};
//#endregion

/** The default size of the similar listings to be displayed in each section. */
export const DEFAULT_SIZE = 6;
export const SIMILAR_LISTINGS_QUERY_KEY = 'similar-listings' as const;
export const SOLD_LISTINGS_QUERY_KEY = 'similar-sold-listings' as const;

//#region Component
export default function SimilarListingsSections({ listing, size = DEFAULT_SIZE, isOffMarket, hideSimilarListings = false, hideComparableListings = false, showCarousel = false, onListingCardClick }: SimilarListingsSectionsProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const { ref, inView } = useInView();
  const { user, acceptedTerms, acceptedBrowserBasedTerms } = useUserContext();


  const select = useCallback((data: SimilarListing[]) => {
    return data.map((listing: SimilarListing) => {
      const complianceRequirement = getComplianceRequirementFromCookies(listing.providerId, user?.id);
      const shouldScramble = shouldScrambleForProvider(listing.providerId, complianceRequirement);

      if (acceptedTerms.length > 0 && acceptedBrowserBasedTerms.length > 0) { // needed to keep callback reactive when accepted terms or browser based terms change
        console.debug('acceptedTerms', acceptedTerms);
        console.debug('acceptedBrowserBasedTerms', acceptedBrowserBasedTerms);
      }

      const complianceData: ComplianceData = {
        compliance: {
          shouldScramble,
          complianceRequirement,
        },
      };
      return new SimilarListingViewModel(listing, complianceData);
    });
  }, [user?.id, acceptedTerms, acceptedBrowserBasedTerms]);

  const similarQuery = useQuery({
    queryKey: [SIMILAR_LISTINGS_QUERY_KEY, listing.id],
    queryFn: async ({ signal }) => {
      const data = await getSimilarListings({
        id: listing.id,
        size,
      }, signal);
      return data;
    },
    enabled: inView && !hideSimilarListings,
    select,
  });

  const similarSoldQuery = useQuery({
    queryKey: [SOLD_LISTINGS_QUERY_KEY, listing.id],
    queryFn: async ({ signal }) => {
      const data = await getSimilarListings({
        id: listing.id,
        size,
        standardStatus: StandardStatus.STANDARD_STATUS_CLOSED,
      }, signal);
      return data;
    },
    enabled: inView && !hideComparableListings,
    select,
  });

  const hideSimilar = hideSimilarListings || similarQuery.isFetched && similarQuery?.data?.length === 0;
  const hideSold = hideComparableListings || similarSoldQuery.isFetched && similarSoldQuery?.data?.length === 0;

  return (
    <div className={styles.component} ref={ref} data-testid={listingPageModalIds.similarListingsSections}>
      {!hideSimilar &&
        <SimilarListings
          heading={t(LOCALE_COMMON_PATHS.ADDRESS_SIMILAR_LISTINGS_SIMILAR_HEADING)}
          componentUsage={SimilarListingsComponentUsage.Similar}
          isLoading={!similarQuery.isFetched}
          listings={similarQuery.data}
          showCarousel={showCarousel}
          onListingCardClick={onListingCardClick}
        />
      }
      {!hideSold &&
        <SimilarListings
          heading={t(LOCALE_COMMON_PATHS.ADDRESS_SIMILAR_LISTINGS_COMPARABLE_HEADING)}
          componentUsage={SimilarListingsComponentUsage.Comparable}
          isLoading={!similarSoldQuery.isFetched}
          listingToCompare={!isOffMarket ? listing : undefined}
          listings={similarSoldQuery.data}
          showCarousel={showCarousel}
          onListingCardClick={onListingCardClick}
        />
      }
    </div>
  );
}
//#endregion
